import CommonImg from "@/components/Common/Img";
import CommonInput from "@/components/Common/Input";
import CommonPagination from "@/components/Common/Pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/libs/utils";
import { Check, Download, Upload, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";

import { SMFItemType } from "@/consts/Estimate";
import { useCanEditSubstituteProducts } from "@/hooks/useIsFinancialManager";
import {
  useCreateReagentAlt,
  useDeleteReagentAlt,
  useReadReagentAlt,
  useUpdateReagentAlt,
} from "@/libs/react-query/hooks/useReagentAltQuery";
import { searchSMF } from "@/services/api/itemsheet";
import { SmfProduct } from "@/types/itemsheet";
import { ReagentAltItem } from "@/types/reagent-alt";
import { showToast } from "@/utils/toastUtils";

import { ReactComponent as ArrowDownSvg } from "@/assets/images/arrow-down.svg";
import { ReactComponent as ArrowUpSvg } from "@/assets/images/arrow-up.svg";
import DeleteCircleIcon from "@/assets/images/delete-circle.svg";
import DeletePinkIcon from "@/assets/images/delete-pink.svg";
import DeleteWhiteIcon from "@/assets/images/delete-white.svg";
import PlusBrownIcon from "@/assets/images/plus-brown.svg";
import PlusWhiteIcon from "@/assets/images/plus-white.svg";
import SearchBlackIcon from "@/assets/images/search-black.svg";

// 엑셀 업로드용 타입
interface ExcelRowData {
  alt_item_no: string;
  alt_item_name: string;
  alt_item_spec?: string;
  alt_name?: string;
  own_item_nos: string[];
  validationStatus?: "pending" | "valid" | "invalid" | "success" | "api_error";
  validationMessage?: string;
}

// 업로드 실패 사유를 사용자에게 보여줄 문장으로 바꾼다.
// API 오류 본문은 { display_message, debug_message, meta_data } 형태이고,
// 서버에 닿지 못한 경우에는 본문 자체가 없다.
const getUploadErrorMessage = (error: any): string => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (!error?.response) {
    return "네트워크 오류로 서버에 연결하지 못했습니다";
  }
  if (status >= 500) {
    return `서버 오류 (${status}) — 잠시 후 다시 시도해 주세요`;
  }

  const missing: string[] | undefined = data?.meta_data?.missing_own_item_nos;
  if (missing?.length) {
    return `마스터에 없는 자사품번호: ${missing.join(", ")}`;
  }

  return data?.display_message || error?.message || `API 오류 (${status})`;
};

// 컬럼 너비 상수
const COLUMN_WIDTHS = {
  index: 60,
  ttp: 200,
  productNumber: 100,
  vendor: 100,
  spec: 80,
  price: 100,
  substitutes: 60,
  ownProducts: 300,
  delete: 60,
};

const PAGE_SIZE = 50;

// 부서 목록 상수
const DEPARTMENTS = [
  { value: "", label: "전체 부서" },
  { value: "BPG", label: "BPG" },
  { value: "TPP", label: "TPP" },
  { value: "LPD SMF", label: "LPD SMF" },
  { value: "BID SERA", label: "BID SERA" },
  { value: "BID", label: "BID" },
  { value: "CSD", label: "CSD" },
  { value: "GSD", label: "GSD" },
  { value: "LSG", label: "LSG" },
  { value: "Others", label: "Others" },
];

// 편집 가능한 셀 컴포넌트
interface EditableCellProps {
  value: string;
  onSave: (value: string) => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
}

function EditableCell({
  value,
  onSave,
  isEditing,
  onStartEdit,
  onCancelEdit,
}: EditableCellProps) {
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onSave(editValue);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditing, editValue, onSave]);

  const handleSave = () => {
    onSave(editValue);
  };

  const handleCancel = () => {
    setEditValue(value);
    onCancelEdit();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div
        ref={containerRef}
        className="flex items-center border rounded focus-within:border-[#7D6B68] bg-white min-w-[150px]"
      >
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 min-w-0 px-2 py-1 text-xs border-none focus:outline-none bg-transparent"
        />
        <div className="flex items-center gap-1 pr-1 shrink-0">
          <button
            type="button"
            onClick={handleCancel}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="w-4 h-4 text-pink" />
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <Check className="w-4 h-4 text-green" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="cursor-pointer hover:bg-muted/50 rounded min-h-[32px] flex items-center"
      onClick={onStartEdit}
    >
      {value || <span className="text-gray-400">-</span>}
    </div>
  );
}

// 자사품 인라인 표시 컴포넌트 (own_items를 직접 받아서 사용)
interface InlineOwnItemsProps {
  altItem: ReagentAltItem;
  onAddOwnItems: (ownItemNos: string[]) => void;
  onDeleteOwnItem: (ownItemNo: string) => void;
}

function InlineOwnItems({
  altItem,
  onAddOwnItems,
  onDeleteOwnItem,
}: InlineOwnItemsProps) {
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [smfProducts, setSmfProducts] = useState<SmfProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOwnItemNos, setSelectedOwnItemNos] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const ownItems = altItem.own_items || [];

  // SMF 제품 검색
  const fetchSmfProducts = useCallback(async (keyword: string) => {
    if (keyword.length < 3) {
      setSmfProducts([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await searchSMF({
        product_id: keyword,
        item_type: SMFItemType.RGNT,
        page: 0,
        limit: 20,
      });
      setSmfProducts(response.products ?? []);
    } catch (error) {
      console.error("SMF 검색 실패:", error);
      setSmfProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 검색어 변경 시 debounce 적용
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchSmfProducts(searchKeyword);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchKeyword, fetchSmfProducts]);

  // 바깥 클릭시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleSelect = (productId: string) => {
    setSelectedOwnItemNos((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSaveOwnItems = () => {
    if (selectedOwnItemNos.length === 0) {
      showToast({ type: "error", message: "자사품을 선택해주세요" });
      return;
    }
    onAddOwnItems(selectedOwnItemNos);
    setIsAddingItem(false);
    setSelectedOwnItemNos([]);
    setSearchKeyword("");
    setIsDropdownOpen(false);
  };

  const handleCancelAdd = () => {
    setIsAddingItem(false);
    setSelectedOwnItemNos([]);
    setSearchKeyword("");
    setIsDropdownOpen(false);
  };

  return (
    <div className="flex flex-col gap-1">
      {/* 자사품 목록 */}
      {ownItems.length > 0 ? (
        <div className="flex flex-col gap-1">
          {ownItems.map((item, index) => (
            <div
              key={item.no}
              className="flex items-center justify-between gap-2 text-sm text-gray-A py-0.5"
            >
              <div className="flex-1 min-w-0 truncate">
                <span className="text-gray-600 mr-2">{index + 1}.</span>
                <span>{item.name || "-"}</span>
                <span className="mx-1">/</span>
                <span>{item.no}</span>
                {item.spec && (
                  <>
                    <span className="mx-1">/</span>
                    <span>{item.spec}</span>
                  </>
                )}
                {item.list_price != null && (
                  <>
                    <span className="mx-1">/</span>
                    <span>
                      {Math.floor(Number(item.list_price)).toLocaleString()}
                    </span>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => onDeleteOwnItem(item.no)}
                className="shrink-0 hover:opacity-80"
              >
                <CommonImg
                  src={DeleteCircleIcon}
                  alt="Delete"
                  className="w-4 h-4"
                />
              </button>
            </div>
          ))}
        </div>
      ) : (
        !isAddingItem && (
          <div className="text-sm text-gray-400">등록된 자사품 없음</div>
        )
      )}

      {/* 자사품 추가 UI */}
      {isAddingItem ? (
        <div className="relative mt-1" ref={dropdownRef}>
          <div
            className="flex items-center justify-between px-2 py-1 rounded border border-dark-red bg-white text-xs cursor-pointer"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <span className="truncate">
              {selectedOwnItemNos.length > 0
                ? `${selectedOwnItemNos.length}개 선택됨`
                : "자사품 선택"}
            </span>
            <ArrowDownSvg className="w-3 h-3 ml-1 shrink-0" />
          </div>

          {isDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-D rounded shadow-lg z-50 max-h-[200px] overflow-hidden flex flex-col">
              <div className="p-2 border-b border-gray-D">
                <input
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  placeholder="3자 이상 입력"
                  className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:border-dark-red"
                  autoFocus
                />
              </div>
              <div className="overflow-y-auto flex-1">
                {isLoading ? (
                  <div className="p-2 text-xs text-center text-gray-500">
                    로딩 중...
                  </div>
                ) : searchKeyword.length < 3 ? (
                  <div className="p-2 text-xs text-center text-gray-500">
                    3자 이상 입력
                  </div>
                ) : smfProducts.length > 0 ? (
                  smfProducts.map((item) => {
                    const isSelected = selectedOwnItemNos.includes(item.id);
                    return (
                      <div
                        key={item.id}
                        className={cn(
                          "px-2 py-1.5 text-xs cursor-pointer hover:bg-gray-100 flex items-center gap-2",
                          isSelected && "bg-gray-100"
                        )}
                        onClick={() => handleToggleSelect(item.id)}
                      >
                        <div
                          className={cn(
                            "w-3 h-3 rounded border flex items-center justify-center shrink-0",
                            isSelected
                              ? "bg-dark-red border-dark-red"
                              : "border-gray-D"
                          )}
                        >
                          {isSelected && (
                            <Check className="w-2 h-2 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0 truncate">
                          <span className="font-medium">
                            {item.name || "-"}
                          </span>
                          <span className="text-gray-500 text-[10px] ml-1">
                            {item.id} {item.spec && `| ${item.spec}`}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-2 text-xs text-center text-gray-500">
                    검색 결과 없음
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 추가/취소 버튼 */}
          <div className="flex items-center gap-1 mt-1">
            <button
              type="button"
              onClick={handleCancelAdd}
              className="px-2 py-0.5 rounded text-[10px] border border-gray-D bg-gray-E text-gray-A hover:opacity-80"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSaveOwnItems}
              className="px-2 py-0.5 rounded text-[10px] bg-dark-red text-white hover:opacity-80"
            >
              추가
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-center mt-1">
          <button
            type="button"
            onClick={() => setIsAddingItem(true)}
            className="flex items-center gap-1 text-xs font-bold hover:opacity-80"
            style={{ color: "#A76D6E" }}
          >
            <span>자사품 추가하기</span>
            <CommonImg src={PlusBrownIcon} alt="Add" className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// 하위 자사품 테이블 컴포넌트
interface InnerOwnItemTableProps {
  altItem: ReagentAltItem;
  onAddOwnItems: (ownItemNos: string[]) => void;
  onDeleteOwnItem: (ownItemNo: string) => void;
}

function InnerOwnItemTable({
  altItem,
  onAddOwnItems,
  onDeleteOwnItem,
}: InnerOwnItemTableProps) {
  const [isAddingRow, setIsAddingRow] = useState(false);
  const [selectedOwnItemNos, setSelectedOwnItemNos] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [smfProducts, setSmfProducts] = useState<SmfProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const ownItems = altItem.own_items || [];

  // SMF 제품 검색
  const fetchSmfProducts = useCallback(async (keyword: string) => {
    if (keyword.length < 3) {
      setSmfProducts([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await searchSMF({
        product_id: keyword,
        item_type: SMFItemType.RGNT,
        page: 0,
        limit: 20,
      });
      setSmfProducts(response.products ?? []);
    } catch (error) {
      console.error("SMF 검색 실패:", error);
      setSmfProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 검색어 변경 시 debounce 적용
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchSmfProducts(searchKeyword);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchKeyword, fetchSmfProducts]);

  // 바깥 클릭시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleSelect = (productId: string) => {
    setSelectedOwnItemNos((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSaveOwnItems = () => {
    if (selectedOwnItemNos.length === 0) {
      showToast({ type: "error", message: "자사품을 선택해주세요" });
      return;
    }
    onAddOwnItems(selectedOwnItemNos);
    setIsAddingRow(false);
    setSelectedOwnItemNos([]);
    setSearchKeyword("");
    setIsDropdownOpen(false);
  };

  const handleCancelAdd = () => {
    setIsAddingRow(false);
    setSelectedOwnItemNos([]);
    setSearchKeyword("");
    setIsDropdownOpen(false);
  };

  return (
    <div className="bg-white overflow-visible">
      <Table style={{ tableLayout: "fixed" }}>
        <TableHeader>
          <TableRow>
            <TableHead
              className="px-2 py-[10px] text-sm text-white bg-gray-A"
              style={{ width: COLUMN_WIDTHS.index }}
            ></TableHead>
            <TableHead
              className="px-2 py-[10px] text-sm text-white bg-gray-A"
              style={{ width: COLUMN_WIDTHS.ttp }}
            >
              자사품명
            </TableHead>
            <TableHead
              className="px-2 py-[10px] text-sm text-white bg-gray-A"
              style={{ width: COLUMN_WIDTHS.productNumber }}
            >
              Sku No.
            </TableHead>
            <TableHead
              className="px-2 py-[10px] text-sm text-white bg-gray-A"
              style={{ width: COLUMN_WIDTHS.vendor }}
            >
              스펙
            </TableHead>
            <TableHead
              className="px-2 py-[10px] text-sm text-white bg-gray-A"
              style={{ width: COLUMN_WIDTHS.price }}
            >
              가격
            </TableHead>
            <TableHead
              className="px-2 py-[10px] text-sm text-white bg-gray-A"
              style={{ width: COLUMN_WIDTHS.substitutes }}
            ></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ownItems.length > 0 ? (
            ownItems.map((item) => (
              <TableRow key={item.no}>
                <TableCell
                  className="px-2"
                  style={{ width: COLUMN_WIDTHS.index }}
                ></TableCell>
                <TableCell
                  className="px-2 text-xs"
                  style={{ width: COLUMN_WIDTHS.ttp }}
                >
                  {item.name || "-"}
                </TableCell>
                <TableCell
                  className="px-2 text-xs"
                  style={{ width: COLUMN_WIDTHS.productNumber }}
                >
                  {item.no}
                </TableCell>
                <TableCell
                  className="px-2 text-xs"
                  style={{ width: COLUMN_WIDTHS.vendor }}
                >
                  {item.spec || "-"}
                </TableCell>
                <TableCell
                  className="px-2 text-xs"
                  style={{ width: COLUMN_WIDTHS.price }}
                >
                  {item.list_price != null
                    ? Math.floor(Number(item.list_price)).toLocaleString()
                    : "-"}
                </TableCell>
                <TableCell
                  className="px-2"
                  style={{ width: COLUMN_WIDTHS.substitutes }}
                >
                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => onDeleteOwnItem(item.no)}
                      className="shrink-0 hover:opacity-80"
                    >
                      <CommonImg
                        src={DeleteCircleIcon}
                        alt="Delete"
                        className="w-5 h-5"
                      />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            !isAddingRow && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-xs py-4">
                  등록된 자사품이 없습니다.
                </TableCell>
              </TableRow>
            )
          )}
          {isAddingRow && (
            <TableRow className="bg-white">
              <TableCell colSpan={6} className="p-0">
                <div className="flex items-start bg-white py-2">
                  <div
                    className="px-2"
                    style={{ width: COLUMN_WIDTHS.index, flexShrink: 0 }}
                  ></div>
                  <div
                    className="px-2 relative"
                    style={{ width: COLUMN_WIDTHS.ttp }}
                    ref={dropdownRef}
                  >
                    <div
                      className="flex items-center justify-between px-2 py-[6px] rounded border border-dark-red bg-white min-h-[32px] cursor-pointer"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                      <span className="flex-1 text-xs truncate">
                        {selectedOwnItemNos.length > 0
                          ? `${selectedOwnItemNos.length}개 선택됨`
                          : "자사품을 선택하세요"}
                      </span>
                      <ArrowDownSvg className="w-4 h-4 ml-2 shrink-0" />
                    </div>
                    {isDropdownOpen && (
                      <div className="absolute top-full left-2 right-2 mt-1 bg-white border border-gray-D rounded shadow-lg z-50 max-h-[250px] overflow-hidden flex flex-col">
                        <div className="p-2 border-b border-gray-D">
                          <input
                            type="text"
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.target.value)}
                            placeholder="3자 이상 입력하세요"
                            className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:border-dark-red"
                            autoFocus
                          />
                        </div>
                        <div className="overflow-y-auto flex-1">
                          {isLoading ? (
                            <div className="p-2 text-xs text-center text-gray-500">
                              로딩 중...
                            </div>
                          ) : searchKeyword.length < 3 ? (
                            <div className="p-2 text-xs text-center text-gray-500">
                              3자 이상 입력하세요
                            </div>
                          ) : smfProducts.length > 0 ? (
                            smfProducts.map((item) => {
                              const isSelected = selectedOwnItemNos.includes(item.id);
                              return (
                                <div
                                  key={item.id}
                                  className={cn(
                                    "px-3 py-2 text-xs cursor-pointer hover:bg-gray-100 flex items-center gap-2",
                                    isSelected && "bg-gray-100"
                                  )}
                                  onClick={() => handleToggleSelect(item.id)}
                                >
                                  <div
                                    className={cn(
                                      "w-4 h-4 rounded border flex items-center justify-center shrink-0",
                                      isSelected
                                        ? "bg-dark-red border-dark-red"
                                        : "border-gray-D"
                                    )}
                                  >
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium truncate">
                                      {item.name || "-"}
                                    </div>
                                    <div className="text-gray-500 text-[10px] truncate">
                                      {item.id} {item.spec && `| ${item.spec}`}
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="p-2 text-xs text-center text-gray-500">
                              검색 결과가 없습니다
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-[3px]">
                    <button
                      type="button"
                      onClick={handleCancelAdd}
                      className="px-2 py-1 rounded-[3.238px] border border-gray-D bg-gray-E text-gray-A text-[10px] font-semibold hover:opacity-80"
                    >
                      취소
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveOwnItems}
                      className="px-2 py-1 rounded-[3.238px] bg-dark-red text-white text-[10px] font-semibold hover:opacity-80"
                    >
                      추가
                    </button>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {/* 추가 버튼 */}
      {!isAddingRow && (
        <div className="flex justify-center py-3 bg-white">
          <button
            type="button"
            onClick={() => setIsAddingRow(true)}
            className="hover:opacity-80"
          >
            <CommonImg src={PlusBrownIcon} alt="Add" className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
}

// 새 행 입력 컴포넌트
interface NewRowProps {
  onSave: (data: {
    alt_item_no: string;
    alt_item_name: string;
    alt_item_spec: string;
    alt_name: string;
    own_item_nos: string[];
  }) => void;
  onCancel: () => void;
  isDeleteMode?: boolean;
}

function NewRow({ onSave, onCancel, isDeleteMode = false }: NewRowProps) {
  const [formData, setFormData] = useState({
    alt_item_name: "",
    alt_item_no: "",
    alt_name: "",
    alt_item_spec: "",
  });
  const [selectedOwnItemNos, setSelectedOwnItemNos] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [smfProducts, setSmfProducts] = useState<SmfProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchSmfProducts = useCallback(async (keyword: string) => {
    if (keyword.length < 3) {
      setSmfProducts([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await searchSMF({
        product_id: keyword,
        item_type: SMFItemType.RGNT,
        page: 0,
        limit: 20,
      });
      setSmfProducts(response.products ?? []);
    } catch (error) {
      console.error("SMF 검색 실패:", error);
      setSmfProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchSmfProducts(searchKeyword);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchKeyword, fetchSmfProducts]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleSelect = (productId: string) => {
    setSelectedOwnItemNos((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSave = () => {
    if (!formData.alt_item_no.trim() || !formData.alt_item_name.trim()) {
      showToast({
        type: "error",
        message: "타사제품 번호와 이름은 필수입니다",
      });
      return;
    }
    if (selectedOwnItemNos.length === 0) {
      showToast({ type: "error", message: "자사품을 최소 1개 선택해주세요" });
      return;
    }
    onSave({ ...formData, own_item_nos: selectedOwnItemNos });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onCancel();
    }
  };

  return (
    <>
      <TableRow className="bg-muted/30">
        <TableCell
          className="px-2 text-center text-gray-400"
          style={{ width: COLUMN_WIDTHS.index }}
        >
          -
        </TableCell>
        <TableCell className="px-2" style={{ width: COLUMN_WIDTHS.ttp }}>
          <input
            type="text"
            value={formData.alt_item_name}
            onChange={(e) =>
              setFormData({ ...formData, alt_item_name: e.target.value })
            }
            onKeyDown={handleKeyDown}
            placeholder="타사제품명 *"
            className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:border-[#7D6B68]"
          />
        </TableCell>
        <TableCell
          className="px-2"
          style={{ width: COLUMN_WIDTHS.productNumber }}
        >
          <input
            type="text"
            value={formData.alt_item_no}
            onChange={(e) =>
              setFormData({ ...formData, alt_item_no: e.target.value })
            }
            onKeyDown={handleKeyDown}
            placeholder="타사제품 번호 *"
            className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:border-[#7D6B68]"
          />
        </TableCell>
        <TableCell className="px-2" style={{ width: COLUMN_WIDTHS.vendor }}>
          <input
            type="text"
            value={formData.alt_name}
            onChange={(e) =>
              setFormData({ ...formData, alt_name: e.target.value })
            }
            onKeyDown={handleKeyDown}
            placeholder="제조사"
            className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:border-[#7D6B68]"
          />
        </TableCell>
        <TableCell className="px-2" style={{ width: COLUMN_WIDTHS.spec }}>
          <input
            type="text"
            value={formData.alt_item_spec}
            onChange={(e) =>
              setFormData({ ...formData, alt_item_spec: e.target.value })
            }
            onKeyDown={handleKeyDown}
            placeholder="규격"
            className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:border-[#7D6B68]"
          />
        </TableCell>
        <TableCell
          className="px-2"
          style={{ width: COLUMN_WIDTHS.substitutes }}
        ></TableCell>
        <TableCell
          className="px-2"
          style={{ width: COLUMN_WIDTHS.ownProducts }}
        ></TableCell>
        {isDeleteMode && (
          <TableCell
            className="px-2"
            style={{ width: COLUMN_WIDTHS.delete }}
          ></TableCell>
        )}
      </TableRow>
      <TableRow className="bg-muted/30 border-t-0">
        <TableCell
          className="px-2"
          style={{ width: COLUMN_WIDTHS.index }}
        ></TableCell>
        <TableCell colSpan={isDeleteMode ? 7 : 6} className="px-2 py-2">
          <div className="flex items-start gap-4">
            <div className="flex-1 relative" ref={dropdownRef}>
              <div className="text-xs text-gray-500 mb-1">
                자사품 선택 * (최소 1개)
              </div>
              <div
                className="flex items-center justify-between px-2 py-[6px] rounded border border-dark-red bg-white min-h-[32px] cursor-pointer"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span className="flex-1 text-xs truncate">
                  {selectedOwnItemNos.length > 0
                    ? `${selectedOwnItemNos.length}개 선택됨`
                    : "자사품을 선택하세요"}
                </span>
                <ArrowDownSvg className="w-4 h-4 ml-2 shrink-0" />
              </div>
              {isDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-D rounded shadow-lg z-50 max-h-[200px] overflow-hidden flex flex-col">
                  <div className="p-2 border-b border-gray-D">
                    <input
                      type="text"
                      value={searchKeyword}
                      onChange={(e) => setSearchKeyword(e.target.value)}
                      placeholder="3자 이상 입력하세요"
                      className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:border-dark-red"
                      autoFocus
                    />
                  </div>
                  <div className="overflow-y-auto flex-1">
                    {isLoading ? (
                      <div className="p-2 text-xs text-center text-gray-500">
                        로딩 중...
                      </div>
                    ) : searchKeyword.length < 3 ? (
                      <div className="p-2 text-xs text-center text-gray-500">
                        3자 이상 입력하세요
                      </div>
                    ) : smfProducts.length > 0 ? (
                      smfProducts.map((item) => {
                        const isSelected = selectedOwnItemNos.includes(item.id);
                        return (
                          <div
                            key={item.id}
                            className={cn(
                              "px-3 py-2 text-xs cursor-pointer hover:bg-gray-100 flex items-center gap-2",
                              isSelected && "bg-gray-100"
                            )}
                            onClick={() => handleToggleSelect(item.id)}
                          >
                            <div
                              className={cn(
                                "w-4 h-4 rounded border flex items-center justify-center shrink-0",
                                isSelected
                                  ? "bg-dark-red border-dark-red"
                                  : "border-gray-D"
                              )}
                            >
                              {isSelected && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">
                                {item.name || "-"}
                              </div>
                              <div className="text-gray-500 text-[10px] truncate">
                                {item.id} {item.spec && `| ${item.spec}`}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-2 text-xs text-center text-gray-500">
                        검색 결과가 없습니다
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 pt-5">
              <button
                type="button"
                onClick={onCancel}
                className="px-3 py-1.5 rounded-[3.238px] border border-gray-D bg-gray-E text-gray-A text-xs font-semibold hover:opacity-80"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-3 py-1.5 rounded-[3.238px] bg-dark-red text-white text-xs font-semibold hover:opacity-80"
              >
                추가
              </button>
            </div>
          </div>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function SubstituteProductList() {
  const canEdit = useCanEditSubstituteProducts();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [isAddingRow, setIsAddingRow] = useState(false);
  const [editingCell, setEditingCell] = useState<{
    alt_item_no: string;
    field: "name" | "spec" | "manufacturer";
  } | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isExcelDialogOpen, setIsExcelDialogOpen] = useState(false);
  const [excelData, setExcelData] = useState<ExcelRowData[]>([]);
  const [isExcelUploading, setIsExcelUploading] = useState(false);
  const [isExcelUploadComplete, setIsExcelUploadComplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 전체 데이터 조회 (한 번만)
  const { data, isLoading } = useReadReagentAlt();

  // Mutations
  const createMutation = useCreateReagentAlt();
  const updateMutation = useUpdateReagentAlt();
  const deleteMutation = useDeleteReagentAlt();

  const allItems = data?.items ?? [];

  // 프론트엔드 필터링
  const filteredItems = useMemo(() => {
    let result = allItems;

    // 검색어 필터
    if (searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase();
      result = result.filter(
        (item) =>
          item.name?.toLowerCase().includes(keyword) ||
          item.no?.toLowerCase().includes(keyword) ||
          item.manufacturer?.toLowerCase().includes(keyword) ||
          item.spec?.toLowerCase().includes(keyword) ||
          item.own_items?.some(
            (own) =>
              own.name?.toLowerCase().includes(keyword) ||
              own.no?.toLowerCase().includes(keyword)
          )
      );
    }

    // 부서 필터
    if (selectedDepartment) {
      result = result.filter((item) =>
        item.own_items?.some((own) => own.division === selectedDepartment)
      );
    }

    return result;
  }, [allItems, searchKeyword, selectedDepartment]);

  // 프론트엔드 페이지네이션
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, currentPage]);

  const totalItems = filteredItems.length;

  // 검색/필터 변경 시 페이지 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [searchKeyword, selectedDepartment]);

  const toggleRow = (alt_item_no: string) => {
    setExpandedRow((prev) => (prev === alt_item_no ? null : alt_item_no));
  };

  const handleAddRow = () => {
    setIsAddingRow(true);
  };

  const handleDeleteModeToggle = () => {
    setIsDeleteMode(!isDeleteMode);
  };

  const handleDeleteClick = (alt_item_no: string) => {
    setDeleteTargetId(alt_item_no);
  };

  const handleConfirmDelete = async () => {
    if (deleteTargetId) {
      try {
        await deleteMutation.mutateAsync({ alt_item_no: deleteTargetId });
        showToast({ type: "success", message: "대체품이 삭제되었습니다" });
        setDeleteTargetId(null);
      } catch (error) {
        showToast({ type: "error", message: "삭제에 실패했습니다" });
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteTargetId(null);
  };

  const handleSaveNewRow = async (newData: {
    alt_item_no: string;
    alt_item_name: string;
    alt_item_spec: string;
    alt_name: string;
    own_item_nos: string[];
  }) => {
    try {
      await createMutation.mutateAsync(newData);
      showToast({ type: "success", message: "대체품이 추가되었습니다" });
      setIsAddingRow(false);
    } catch (error) {
      showToast({ type: "error", message: "추가에 실패했습니다" });
    }
  };

  const handleCancelNewRow = () => {
    setIsAddingRow(false);
  };

  const handleStartEdit = (
    alt_item_no: string,
    field: "name" | "spec" | "manufacturer"
  ) => {
    if (!canEdit) return;
    setEditingCell({ alt_item_no, field });
  };

  const handleUpdateCell = useCallback(
    async (
      item: ReagentAltItem,
      field: "name" | "spec" | "manufacturer",
      value: string
    ) => {
      if (item[field] === value) {
        setEditingCell(null);
        return;
      }

      try {
        await updateMutation.mutateAsync({
          alt_item_no: item.no,
          alt_item_name: field === "name" ? value : item.name,
          alt_item_spec: field === "spec" ? value : item.spec,
          alt_name: field === "manufacturer" ? value : item.manufacturer,
        });
        showToast({ type: "success", message: "수정되었습니다" });
      } catch (error) {
        showToast({ type: "error", message: "수정에 실패했습니다" });
      }
      setEditingCell(null);
    },
    [updateMutation]
  );

  // 자사품 추가
  const handleAddOwnItems = async (
    altItem: ReagentAltItem,
    ownItemNos: string[]
  ) => {
    try {
      await createMutation.mutateAsync({
        alt_item_no: altItem.no,
        alt_item_name: altItem.name,
        alt_item_spec: altItem.spec,
        alt_name: altItem.manufacturer,
        own_item_nos: ownItemNos,
      });
      showToast({
        type: "success",
        message: `${ownItemNos.length}개 자사품이 추가되었습니다`,
      });
    } catch (error) {
      showToast({ type: "error", message: "자사품 추가에 실패했습니다" });
    }
  };

  // 자사품 삭제
  const handleDeleteOwnItem = async (altItemNo: string, ownItemNo: string) => {
    try {
      await deleteMutation.mutateAsync({
        alt_item_no: altItemNo,
        own_item_nos: [ownItemNo],
      });
      showToast({ type: "success", message: "자사품이 삭제되었습니다" });
    } catch (error) {
      showToast({ type: "error", message: "자사품 삭제에 실패했습니다" });
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleDepartmentChange = (department: string) => {
    setSelectedDepartment(department);
    setCurrentPage(1);
  };

  // 엑셀 파일 선택
  const handleExcelUpload = () => {
    fileInputRef.current?.click();
  };

  // 엑셀 파일 파싱
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const parsedData: ExcelRowData[] = jsonData.map((row: any) => {
          const alt_item_no = String(
            row["타사제품 번호"] ||
              row["타사제품번호"] ||
              row["대체품번호"] ||
              row["alt_item_no"] ||
              ""
          ).trim();
          const alt_item_name = String(
            row["타사제품명"] || row["대체품명"] || row["alt_item_name"] || ""
          ).trim();
          const alt_item_spec = String(
            row["규격"] || row["스펙"] || row["alt_item_spec"] || ""
          ).trim();
          const alt_name = String(
            row["제조사"] || row["벤더사"] || row["alt_name"] || ""
          ).trim();
          const own_item_nos = String(
            row["자사품번호"] || row["own_item_nos"] || ""
          )
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);

          let validationStatus: "valid" | "invalid" = "valid";
          let validationMessage = "";

          if (!alt_item_no) {
            validationStatus = "invalid";
            validationMessage = "타사제품 번호 누락";
          } else if (!alt_item_name) {
            validationStatus = "invalid";
            validationMessage = "타사제품명 누락";
          } else if (own_item_nos.length === 0) {
            validationStatus = "invalid";
            validationMessage = "자사품번호 누락";
          }

          return {
            alt_item_no,
            alt_item_name,
            alt_item_spec,
            alt_name,
            own_item_nos,
            validationStatus,
            validationMessage,
          };
        });

        if (parsedData.length === 0) {
          showToast({
            type: "error",
            message: "파싱된 데이터가 없습니다.",
          });
          return;
        }

        setExcelData(parsedData);
        setIsExcelDialogOpen(true);
      } catch (error) {
        console.error("엑셀 파싱 오류:", error);
        showToast({
          type: "error",
          message: "엑셀 파일을 읽는 중 오류가 발생했습니다.",
        });
      }
    };
    reader.readAsArrayBuffer(file);

    e.target.value = "";
  };

  // 엑셀 데이터 일괄 업로드
  const handleExcelSubmit = async () => {
    const validRows = excelData.filter(
      (row) => row.validationStatus === "valid"
    );

    if (validRows.length === 0) {
      showToast({
        type: "error",
        message: "업로드할 유효한 데이터가 없습니다.",
      });
      return;
    }

    setIsExcelUploading(true);
    setIsExcelUploadComplete(false);

    for (let i = 0; i < excelData.length; i++) {
      const row = excelData[i];

      if (row.validationStatus !== "valid") {
        continue;
      }

      try {
        await createMutation.mutateAsync({
          alt_item_no: row.alt_item_no,
          alt_item_name: row.alt_item_name,
          alt_item_spec: row.alt_item_spec || "",
          alt_name: row.alt_name || "",
          own_item_nos: row.own_item_nos,
        });

        setExcelData((prev) =>
          prev.map((r, idx) =>
            idx === i
              ? {
                  ...r,
                  validationStatus: "success" as const,
                  validationMessage: "업로드 성공",
                }
              : r
          )
        );
      } catch (error: any) {
        const errorMessage = getUploadErrorMessage(error);
        setExcelData((prev) =>
          prev.map((r, idx) =>
            idx === i
              ? {
                  ...r,
                  validationStatus: "api_error" as const,
                  validationMessage: errorMessage,
                }
              : r
          )
        );
        console.error(`업로드 실패: ${row.alt_item_no}`, error);
      }
    }

    setIsExcelUploading(false);
    setIsExcelUploadComplete(true);
  };

  const handleExcelComplete = () => {
    setIsExcelDialogOpen(false);
    setExcelData([]);
    setIsExcelUploadComplete(false);

    showToast({
      type: "success",
      message: "제품 업로드가 완료되었습니다.",
    });
  };

  // 예시 엑셀 다운로드
  const handleDownloadTemplate = () => {
    const templateData = [
      {
        "타사제품 번호": "ALT001",
        타사제품명: "타사제품 예시 1",
        제조사: "제조사 A",
        규격: "100ml",
        자사품번호: "OWN001,OWN002",
      },
      {
        "타사제품 번호": "ALT002",
        타사제품명: "타사제품 예시 2",
        제조사: "제조사 B",
        규격: "200ml",
        자사품번호: "OWN003",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "대체품목록");

    worksheet["!cols"] = [
      { wch: 15 },
      { wch: 20 },
      { wch: 15 },
      { wch: 10 },
      { wch: 25 },
    ];

    XLSX.writeFile(workbook, "대체품_업로드_양식.xlsx");
  };

  // 필터된 전체 데이터 엑셀 다운로드
  const handleDownloadData = () => {
    if (filteredItems.length === 0) {
      showToast({ type: "error", message: "다운로드할 데이터가 없습니다." });
      return;
    }

    const exportData = filteredItems.map((item) => ({
      "타사제품 번호": item.no,
      타사제품명: item.name,
      제조사: item.manufacturer || "",
      규격: item.spec || "",
      자사품번호: item.own_items?.map((o) => o.no).join(", ") || "",
      자사품명: item.own_items?.map((o) => o.name || "-").join(", ") || "",
      자사품가격:
        item.own_items
          ?.map((o) =>
            o.list_price != null
              ? Math.floor(Number(o.list_price)).toLocaleString()
              : "-"
          )
          .join(", ") || "",
      디비전: item.own_items?.map((o) => o.division || "-").join(", ") || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "대체품목록");

    worksheet["!cols"] = [
      { wch: 15 },
      { wch: 25 },
      { wch: 15 },
      { wch: 12 },
      { wch: 25 },
      { wch: 30 },
      { wch: 20 },
      { wch: 15 },
    ];

    const fileName = selectedDepartment
      ? `대체품_목록_${selectedDepartment}.xlsx`
      : "대체품_목록_전체.xlsx";
    XLSX.writeFile(workbook, fileName);
    showToast({ type: "success", message: `${filteredItems.length}건 다운로드 완료` });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-D">
      {/* 카드 헤더 */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-D">
        <h2 className="text-lg font-semibold text-gray-A">대체품 목록</h2>
        <div className="flex items-center gap-2">
          {/* 부서 필터 */}
          <select
            value={selectedDepartment}
            onChange={(e) => handleDepartmentChange(e.target.value)}
            className="h-[30px] px-[8px] py-[4px] text-[10px] border border-gray-D rounded focus:outline-none focus:border-dark-red bg-white"
          >
            {DEPARTMENTS.map((dept) => (
              <option key={dept.value} value={dept.value}>
                {dept.label}
              </option>
            ))}
          </select>
          {/* 검색 */}
          <div className="flex items-center gap-2">
            <CommonInput
              placeholder="검색어를 입력하세요"
              defaultValue={searchKeyword}
              onChange={setSearchKeyword}
              onKeyDownEnter={handleSearch}
            />
            <button
              type="button"
              onClick={handleSearch}
              className="p-2 bg-dark-brown rounded hover:opacity-90"
            >
              <CommonImg
                src={SearchBlackIcon}
                alt="Search"
                className="w-4 h-4 invert"
              />
            </button>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={handleAddRow}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded hover:opacity-90"
              style={{ backgroundColor: "#7D6B68" }}
            >
              <CommonImg src={PlusWhiteIcon} alt="Add" className="w-5 h-5" />
              대체품 추가
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={handleDeleteModeToggle}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded hover:opacity-90"
              style={{ backgroundColor: "#EE8E94" }}
            >
              <CommonImg
                src={DeleteWhiteIcon}
                alt="Delete"
                className="w-5 h-5"
              />
              대체품 삭제
            </button>
          )}
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded hover:opacity-90"
            style={{ backgroundColor: "#5B9BD5" }}
          >
            <Download className="w-5 h-5" />
            엑셀 양식 다운로드
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={handleExcelUpload}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded hover:opacity-90"
              style={{ backgroundColor: "#6CBAA2" }}
            >
              <Upload className="w-5 h-5" />
              엑셀 업로드
            </button>
          )}
          <button
            type="button"
            onClick={handleDownloadData}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded hover:opacity-90"
            style={{ backgroundColor: "#70AD47" }}
          >
            <Download className="w-5 h-5" />
            엑셀 다운로드
          </button>
        </div>
      </div>

      {/* 테이블 */}
      <div className="overflow-visible rounded-md shadow-xl mx-6 my-4">
        <Table style={{ tableLayout: "fixed" }}>
          <TableHeader className="sticky top-0 z-10">
            <TableRow>
              <TableHead
                className="px-2 text-sm text-white bg-dark-red-60 first:rounded-tl-md"
                style={{ width: COLUMN_WIDTHS.index }}
              ></TableHead>
              <TableHead
                className="px-2 text-sm text-white bg-dark-red-60"
                style={{ width: COLUMN_WIDTHS.ttp }}
              >
                타사제품명
              </TableHead>
              <TableHead
                className="px-2 text-sm text-white bg-dark-red-60"
                style={{ width: COLUMN_WIDTHS.productNumber }}
              >
                타사제품 번호
              </TableHead>
              <TableHead
                className="px-2 text-sm text-white bg-dark-red-60"
                style={{ width: COLUMN_WIDTHS.vendor }}
              >
                제조사
              </TableHead>
              <TableHead
                className="px-2 text-sm text-white bg-dark-red-60"
                style={{ width: COLUMN_WIDTHS.spec }}
              >
                규격
              </TableHead>
              <TableHead
                className="px-2 text-sm text-white bg-dark-red-60 text-center"
                style={{ width: COLUMN_WIDTHS.substitutes }}
              >
                펼치기
              </TableHead>
              <TableHead
                className={cn(
                  "px-2 text-sm text-white bg-dark-red-60",
                  !isDeleteMode && "last:rounded-tr-md"
                )}
                style={{ width: COLUMN_WIDTHS.ownProducts }}
              >
                자사품
              </TableHead>
              {isDeleteMode && (
                <TableHead
                  className="px-2 text-sm text-white bg-dark-red-60 last:rounded-tr-md text-center"
                  style={{ width: COLUMN_WIDTHS.delete }}
                ></TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isAddingRow && (
              <NewRow
                onSave={handleSaveNewRow}
                onCancel={handleCancelNewRow}
                isDeleteMode={isDeleteMode}
              />
            )}
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={isDeleteMode ? 8 : 7}
                  className="h-24 text-center"
                >
                  로딩 중...
                </TableCell>
              </TableRow>
            ) : paginatedItems.length > 0 ? (
              paginatedItems.map((item, index) => (
                <React.Fragment key={item.no}>
                  <TableRow
                    className={index % 2 === 0 ? "bg-white" : "bg-light-gray"}
                  >
                    <TableCell
                      className="px-2 text-gray-500"
                      style={{ width: COLUMN_WIDTHS.index }}
                    >
                      {(currentPage - 1) * PAGE_SIZE + index + 1}
                    </TableCell>
                    <TableCell
                      className="px-2"
                      style={{ width: COLUMN_WIDTHS.ttp }}
                    >
                      <EditableCell
                        value={item.name}
                        isEditing={
                          editingCell?.alt_item_no === item.no &&
                          editingCell?.field === "name"
                        }
                        onStartEdit={() =>
                          handleStartEdit(item.no, "name")
                        }
                        onCancelEdit={() => setEditingCell(null)}
                        onSave={(value) =>
                          handleUpdateCell(item, "name", value)
                        }
                      />
                    </TableCell>
                    <TableCell
                      className="px-2"
                      style={{ width: COLUMN_WIDTHS.productNumber }}
                    >
                      <span className="text-xs">
                        {!item.no || item.no === "#N/A"
                          ? "-"
                          : item.no}
                      </span>
                    </TableCell>
                    <TableCell
                      className="px-2"
                      style={{ width: COLUMN_WIDTHS.vendor }}
                    >
                      <EditableCell
                        value={item.manufacturer}
                        isEditing={
                          editingCell?.alt_item_no === item.no &&
                          editingCell?.field === "manufacturer"
                        }
                        onStartEdit={() =>
                          handleStartEdit(item.no, "manufacturer")
                        }
                        onCancelEdit={() => setEditingCell(null)}
                        onSave={(value) =>
                          handleUpdateCell(item, "manufacturer", value)
                        }
                      />
                    </TableCell>
                    <TableCell
                      className="px-2"
                      style={{ width: COLUMN_WIDTHS.spec }}
                    >
                      <EditableCell
                        value={item.spec}
                        isEditing={
                          editingCell?.alt_item_no === item.no &&
                          editingCell?.field === "spec"
                        }
                        onStartEdit={() =>
                          handleStartEdit(item.no, "spec")
                        }
                        onCancelEdit={() => setEditingCell(null)}
                        onSave={(value) =>
                          handleUpdateCell(item, "spec", value)
                        }
                      />
                    </TableCell>
                    <TableCell
                      className="px-2"
                      style={{ width: COLUMN_WIDTHS.substitutes }}
                    >
                      <div className="flex items-center justify-center">
                        <button
                          onClick={() => toggleRow(item.no)}
                          className="shrink-0"
                        >
                          {expandedRow === item.no ? (
                            <ArrowUpSvg className="w-5 h-5" />
                          ) : (
                            <ArrowDownSvg className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </TableCell>
                    <TableCell
                      className="px-2 py-2 align-top"
                      style={{ width: COLUMN_WIDTHS.ownProducts }}
                    >
                      <InlineOwnItems
                        altItem={item}
                        onAddOwnItems={(ownItemNos) =>
                          handleAddOwnItems(item, ownItemNos)
                        }
                        onDeleteOwnItem={(ownItemNo) =>
                          handleDeleteOwnItem(item.no, ownItemNo)
                        }
                      />
                    </TableCell>
                    {isDeleteMode && (
                      <TableCell
                        className="px-2"
                        style={{ width: COLUMN_WIDTHS.delete }}
                      >
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => handleDeleteClick(item.no)}
                            className="shrink-0 hover:opacity-80"
                          >
                            <CommonImg
                              src={DeletePinkIcon}
                              alt="Delete"
                              className="w-5 h-5"
                            />
                          </button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  {expandedRow === item.no && (
                    <TableRow>
                      <TableCell colSpan={isDeleteMode ? 8 : 7} className="p-0">
                        <InnerOwnItemTable
                          altItem={item}
                          onAddOwnItems={(ownItemNos) =>
                            handleAddOwnItems(item, ownItemNos)
                          }
                          onDeleteOwnItem={(ownItemNo) =>
                            handleDeleteOwnItem(item.no, ownItemNo)
                          }
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={isDeleteMode ? 8 : 7}
                  className="h-24 text-center"
                >
                  데이터가 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* 페이지네이션 */}
      <div className="px-6 py-4">
        <CommonPagination
          totalItems={totalItems}
          itemsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* 삭제 확인 모달 */}
      <Dialog
        open={!!deleteTargetId}
        onOpenChange={(open) => !open && handleCancelDelete()}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>대체품 삭제</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-center py-4">
            정말로 삭제하시겠어요?
            <br />
            <span className="text-xs text-gray-500">
              해당 대체품에 매핑된 모든 자사품 연결이 삭제됩니다.
            </span>
          </DialogDescription>
          <DialogFooter>
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="px-4 py-2 rounded-md bg-[#EE8E94] text-white text-sm font-semibold hover:opacity-80 disabled:opacity-50"
            >
              {deleteMutation.isPending ? "삭제 중..." : "삭제하기"}
            </button>
            <button
              type="button"
              onClick={handleCancelDelete}
              className="px-4 py-2 rounded-md border border-gray-D bg-gray-E text-gray-A text-sm font-semibold hover:opacity-80"
            >
              취소하기
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 엑셀 파일 입력 (hidden) */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".xlsx,.xls"
        className="hidden"
      />

      {/* 엑셀 업로드 확인 모달 */}
      <Dialog
        open={isExcelDialogOpen}
        onOpenChange={(open) => {
          if (!open && !isExcelUploading) {
            if (isExcelUploadComplete) {
              handleExcelComplete();
            } else {
              setIsExcelDialogOpen(false);
              setExcelData([]);
            }
          }
        }}
      >
        <DialogContent className="max-w-5xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {isExcelUploadComplete ? "업로드 결과" : "엑셀 데이터 확인"}
            </DialogTitle>
            <DialogDescription>
              <div className="flex items-center gap-4">
                <span>전체: {excelData.length}건</span>
                <span className="text-green flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  성공:{" "}
                  {
                    excelData.filter((r) => r.validationStatus === "success")
                      .length
                  }
                  건
                </span>
                <span className="text-pink flex items-center gap-1">
                  <X className="w-4 h-4" />
                  실패:{" "}
                  {
                    excelData.filter(
                      (r) =>
                        r.validationStatus === "api_error" ||
                        r.validationStatus === "invalid"
                    ).length
                  }
                  건
                </span>
              </div>
            </DialogDescription>
          </DialogHeader>

          {/* 미리보기 테이블 */}
          <div className="flex-1 overflow-auto border rounded-md">
            <Table>
              <TableHeader className="sticky top-0">
                <TableRow>
                  <TableHead className="text-xs text-white bg-gray-A">
                    타사제품 번호
                  </TableHead>
                  <TableHead className="text-xs text-white bg-gray-A">
                    타사제품명
                  </TableHead>
                  <TableHead className="text-xs text-white bg-gray-A">
                    제조사
                  </TableHead>
                  <TableHead className="text-xs text-white bg-gray-A">
                    규격
                  </TableHead>
                  <TableHead className="text-xs text-white bg-gray-A">
                    자사품번호
                  </TableHead>
                  <TableHead className="text-xs text-white bg-gray-A w-[60px]">
                    상태
                  </TableHead>
                  <TableHead className="text-xs text-white bg-gray-A w-[260px]">
                    사유
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {excelData.map((row, idx) => (
                  <TableRow
                    key={idx}
                    className={cn(
                      row.validationStatus === "invalid" && "bg-red-50",
                      row.validationStatus === "api_error" && "bg-red-50",
                      row.validationStatus === "success" && "bg-green-50"
                    )}
                  >
                    <TableCell className="text-xs">
                      {row.alt_item_no || "-"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.alt_item_name || "-"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.alt_name || "-"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.alt_item_spec || "-"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.own_item_nos.length > 0
                        ? row.own_item_nos.join(", ")
                        : "-"}
                    </TableCell>
                    <TableCell className="text-xs text-center">
                      {row.validationStatus === "success" ? (
                        <Check className="w-5 h-5 mx-auto text-green" />
                      ) : row.validationStatus === "valid" ? (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 mx-auto text-pink" />
                      )}
                    </TableCell>
                    <TableCell
                      className="text-xs text-pink break-words"
                      title={row.validationMessage}
                    >
                      {row.validationStatus === "invalid" ||
                      row.validationStatus === "api_error"
                        ? row.validationMessage || "-"
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <DialogFooter className="flex justify-center gap-2">
            {isExcelUploadComplete ? (
              <button
                type="button"
                onClick={handleExcelComplete}
                className="px-6 py-2 rounded-md bg-[#6CBAA2] text-white text-sm font-semibold hover:opacity-80"
              >
                완료
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setIsExcelDialogOpen(false);
                    setExcelData([]);
                    setIsExcelUploadComplete(false);
                  }}
                  disabled={isExcelUploading}
                  className="px-4 py-2 rounded-md border border-gray-D bg-gray-E text-gray-A text-sm font-semibold hover:opacity-80 disabled:opacity-50"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleExcelSubmit}
                  disabled={isExcelUploading}
                  className="px-4 py-2 rounded-md bg-[#6CBAA2] text-white text-sm font-semibold hover:opacity-80 disabled:opacity-50"
                >
                  {isExcelUploading ? "업로드 중..." : "업로드"}
                </button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
